/**
 * Fahryo GA4 Integration with Consent Mode v2
 *
 * Privacy-by-default: Consent Mode is initialised in default-deny state in
 * client/index.html before this module loads. GA4 collects nothing until the
 * user grants analytics consent.
 *
 * Consent strategy (binary banner → granular gtag flags):
 * - User accepts ConsentBanner → analytics_storage = granted
 * - All ad-related categories stay denied (no targeting, no remarketing).
 * - User declines → all categories stay denied.
 *
 * This module is a no-op when VITE_GA4_MEASUREMENT_ID is unset, so dev
 * environments and CI never load gtag. Production builds inject the
 * measurement ID at build time via Vite's `import.meta.env`.
 *
 * IP anonymisation is on by default in GA4 (no `anonymize_ip` flag needed —
 * GA4 truncates IPs server-side automatically and never logs them).
 */

const MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;

// Synchronously-readable consent flag (also gates Sentry Session Replay/tracing
// at app boot, before the user record is loaded). Written by setAnalyticsConsent.
export const TRACKING_CONSENT_KEY = 'fahryo_tracking_consent';

/** True only if the user has explicitly granted analytics/tracking consent. */
export function hasTrackingConsent(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(TRACKING_CONSENT_KEY) === '1';
  } catch {
    return false;
  }
}

type ConsentState = 'granted' | 'denied';

interface ConsentMap {
  analytics_storage?: ConsentState;
  ad_storage?: ConsentState;
  ad_user_data?: ConsentState;
  ad_personalization?: ConsentState;
  functionality_storage?: ConsentState;
  personalization_storage?: ConsentState;
  security_storage?: ConsentState;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __FAHRYO_GA4_LOADED__?: boolean;
  }
}

function gtag(...args: unknown[]): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

/**
 * Lazy-load the gtag script the first time consent is granted.
 * Subsequent calls are idempotent.
 */
function loadGA4Script(): void {
  if (typeof window === 'undefined') return;
  if (!MEASUREMENT_ID) return;
  if (window.__FAHRYO_GA4_LOADED__) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // gtag('js', new Date()) and gtag('config', ...) must run after script load.
  // Pushing them to dataLayer pre-load is safe — the gtag.js bootstrap
  // replays the queue.
  gtag('js', new Date());
  gtag('config', MEASUREMENT_ID, {
    // Anonymise sensitive query strings before they hit GA4
    send_page_view: false, // we trigger explicit events from analytics.ts
    allow_google_signals: false, // disable advertising features
    allow_ad_personalization_signals: false,
  });

  window.__FAHRYO_GA4_LOADED__ = true;
}

/**
 * Update Consent Mode v2 categories. The default-deny state is set in
 * client/index.html before this module executes. Call this whenever the
 * user changes their consent preference.
 */
export function setAnalyticsConsent(granted: boolean): void {
  if (typeof window === 'undefined') return;

  // Persist the choice synchronously so Sentry (and a returning session) can
  // read the tracking-consent state at boot. Written even when GA4 is not
  // configured, because it also gates Sentry Session Replay/tracing.
  try {
    window.localStorage.setItem(TRACKING_CONSENT_KEY, granted ? '1' : '0');
  } catch {
    /* storage unavailable — non-fatal */
  }

  if (!MEASUREMENT_ID) return;

  const consent: ConsentMap = {
    analytics_storage: granted ? 'granted' : 'denied',
    // Keep ad categories denied even when user accepts — Fahryo does not
    // run ads or remarketing. If that ever changes, expose granular toggles
    // in the consent UI first.
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  };

  gtag('consent', 'update', consent);

  if (granted) {
    loadGA4Script();
  }
}

/**
 * Send a custom event to GA4. No-op when gtag is not loaded (i.e. consent
 * not granted or measurement ID not configured).
 *
 * Caller is responsible for passing only non-PII parameters.
 */
export function trackGA4(
  eventName: string,
  params?: Record<string, string | number | boolean>
): void {
  if (typeof window === 'undefined') return;
  if (!MEASUREMENT_ID) return;
  if (!window.__FAHRYO_GA4_LOADED__) return;

  gtag('event', eventName, params);
}

/**
 * Returns true when a measurement ID is configured. Used by the UI to
 * decide whether to show the analytics consent toggle at all.
 */
export function isGA4Configured(): boolean {
  return Boolean(MEASUREMENT_ID);
}
